import { yupResolver } from '@hookform/resolvers/yup'
import { Typography } from '@/app/components/Typography'
import { useMemo, useRef } from 'react'
import * as yup from 'yup'

import {
  Controller,
  FormProvider,
  useForm,
  type Resolver,
} from 'react-hook-form'

import { ErrorMessage } from '@/app/components/ErrorMessage'
import { Input } from '@/app/components/Input'
import {
  INotificationStatus,
  useNotificationContext,
} from '@/app/components/Notification'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { Headline2 } from '@/app/components/Typography'
import { useCompanyIndustries } from '@/app/hooks/data-hooks/useCompanyIndustries'
import { useMyUser } from '@/app/hooks/data-hooks/useMyUser'
import { useUserProfile } from '@/app/hooks/data-hooks/useUserProfile'
import { useUserProfileMutation } from '@/app/hooks/data-hooks/useUserProfileMutation'
import { useUserMutation } from '@/app/hooks/data-hooks/useUserMutation'
import { useWorkAreas } from '@/app/hooks/data-hooks/useWorkAreas'
import { getErrorMessage } from '@/app/lib/getErrorMessage'
import { getMuiIconByName } from '@/app/lib/muiIconFromName'
import Apps from '@mui/icons-material/Apps'
import { Checkbox } from '@/app/modules/shared/components/checkbox'
import { ProgressBar } from '@/app/modules/shared/components/progress-bar'
import { SeniorityLevel } from '@/types/profile'
import { useNamespacedIntl } from '../lib/useNamespacedIntl'

const INDUSTRY_SLUG_ICON_TONE: Record<
  string,
  { boxClass: string; iconClass: string }
> = {
  'technology-saas': { boxClass: 'bg-purple-100', iconClass: 'text-purple-700' },
  'finance-banking': { boxClass: 'bg-sky-100', iconClass: 'text-sky-700' },
  education: { boxClass: 'bg-amber-100', iconClass: 'text-amber-800' },
  'e-commerce-retail': {
    boxClass: 'bg-orange-100',
    iconClass: 'text-orange-700',
  },
  healthcare: { boxClass: 'bg-rose-100', iconClass: 'text-rose-700' },
  consulting: { boxClass: 'bg-blue-100', iconClass: 'text-blue-700' },
  'energy-utilities': {
    boxClass: 'bg-emerald-100',
    iconClass: 'text-emerald-700',
  },
  'logistics-transportation': {
    boxClass: 'bg-fuchsia-100',
    iconClass: 'text-fuchsia-700',
  },
}

export type UserAboutYourRoleStepValues = {
  jobRole: string
  workArea: string
  seniorityLevel: SeniorityLevel | ''
  companyIndustry: string
}

type UserAboutYourRoleStepProps = {
  onContinue: () => void | Promise<void>
  maxSteps: number
  currentStep: number
}

function refId(item: { _id?: string; id?: string }) {
  return item._id ?? item.id ?? ''
}

function refLabel(item: { name?: string; label?: string; title?: string }) {
  return item.name ?? item.label ?? item.title ?? ''
}

function industryIconTone(slug: string | undefined) {
  const key = slug?.toLowerCase().trim() ?? ''
  return (
    INDUSTRY_SLUG_ICON_TONE[key] ?? {
      boxClass: 'bg-gray-100',
      iconClass: 'text-gray-700',
    }
  )
}

function CompanyIndustryListIcon({
  iconName,
  iconClass,
}: {
  iconName: string | undefined
  iconClass: string
}) {
  const Icon = getMuiIconByName(iconName) ?? Apps
  return <Icon sx={{ fontSize: 18 }} className={iconClass} aria-hidden />
}

export function UserAboutYourRoleStep({
  onContinue,
  maxSteps,
  currentStep,
}: UserAboutYourRoleStepProps) {
  const t = useNamespacedIntl('UserOnboarding')
  const { setMessage } = useNotificationContext()
  const { data: { data: user } = {}, isLoading: isLoadingUser } = useMyUser()
  const { data: { data: userProfile } = {} } = useUserProfile()
  const { mutateAsync: updateProfile, isLoading: isMutatingProfile } =
    useUserProfileMutation(userProfile?._id as string)
  const { mutateAsync: updateUser, isLoading: isMutatingUser } = useUserMutation(
    user?._id ?? ''
  )

  const { data: { data: workAreas = [] } = {}, isLoading: isLoadingWorkAreas } =
    useWorkAreas()
  const {
    data: { data: companyIndustries = [] } = {},
    isLoading: isLoadingIndustries,
  } = useCompanyIndustries()

  const industriesForGrid = useMemo(
    () =>
      companyIndustries.filter(
        (i) => (i.slug?.toLowerCase().trim() ?? '') !== 'other'
      ),
    [companyIndustries]
  )

  const seniorityOptions = useMemo(
    () =>
      [
        { value: SeniorityLevel.Junior, label: t('role_seniority_junior') },
        { value: SeniorityLevel.MidLevel, label: t('role_seniority_mid') },
        { value: SeniorityLevel.Senior, label: t('role_seniority_senior') },
        {
          value: SeniorityLevel.StaffLead,
          label: t('role_seniority_staff_lead'),
        },
        { value: SeniorityLevel.HeadVp, label: t('role_seniority_head_vp') },
      ] as const,
    [t]
  )

  const formSchema = yup.object<UserAboutYourRoleStepValues>().shape({
    jobRole: yup.string().default(''),
    workArea: yup.string().required(t('validation_work_area_required')),
    seniorityLevel: yup
      .string()
      .oneOf(Object.values(SeniorityLevel))
      .required(t('validation_seniority_required')),
    companyIndustry: yup
      .string()
      .required(t('validation_company_industry_required')),
  })

  const isMutating = isMutatingProfile || isMutatingUser

  const methods = useForm<UserAboutYourRoleStepValues>({
    resolver: yupResolver(formSchema) as Resolver<UserAboutYourRoleStepValues>,
    disabled: isMutating || isLoadingUser,
    values: {
      jobRole: user?.jobRole ?? '',
      workArea: userProfile?.workArea ?? '',
      seniorityLevel:
        userProfile?.seniorityLevel ?? ('' as SeniorityLevel | ''),
      companyIndustry: userProfile?.companyIndustry ?? '',
    },
  })

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = methods

  const formRef = useRef<HTMLFormElement>(null)
  const selectedIndustryId = watch('companyIndustry')

  const selectIndustry = (id: string) => {
    methods.setValue('companyIndustry', id)
  }

  const handleOnSubmit = async (data: UserAboutYourRoleStepValues) => {
    try {
      if (user?._id) {
        await updateUser({
          firstName: user.firstName,
          lastName: user.lastName,
          jobRole: data.jobRole,
          linkedIn: user.linkedIn,
        })
      }
      await updateProfile({
        workArea: data.workArea,
        seniorityLevel: data.seniorityLevel as SeniorityLevel,
        companyIndustry: data.companyIndustry,
      })
      await onContinue()
    } catch {
      setMessage(t('profile_update_failed'), INotificationStatus.ERROR)
    }
  }

  const listsLoading = isLoadingWorkAreas || isLoadingIndustries

  return (
    <FormProvider {...methods}>
      <form
        className="flex px-6 pb-3 md:px-0 md:py-8"
        onSubmit={handleSubmit(handleOnSubmit)}
        ref={formRef}
      >
        <div className="flex w-full flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Headline2>{t('about_role_heading')}</Headline2>
            <p className="text-left text-sm text-primary-light">
              {t('about_role_subtitle')}
            </p>
          </div>

          {errors.root?.serverError?.message && (
            <ErrorMessage
              message={errors.root?.serverError?.message}
              className="mt-2 text-left"
            />
          )}

          <div className="flex flex-col gap-2">
            <label
              htmlFor="jobRole"
              className="text-sm font-semibold text-secondary"
            >
              {t('about_role_job_role_label')}
            </label>
            <Controller
              control={control}
              name="jobRole"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id="jobRole"
                  placeholder={t('about_role_job_role_placeholder')}
                  errorMessage={getErrorMessage('jobRole', errors)}
                  className="w-full text-sm"
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="workArea"
              className="text-sm font-semibold text-secondary"
            >
              {t('about_role_area_label')}
            </label>
            <Controller
              control={control}
              name="workArea"
              render={({ field }) => (
                <div className="relative">
                  <select
                    {...field}
                    id="workArea"
                    disabled={isMutating || listsLoading}
                    className="h-12 w-full appearance-none rounded-xl border border-lines bg-white pl-4 pr-10 text-sm text-mydra-black outline-none focus:border-mydra-purple disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <option value="">
                      {listsLoading
                        ? t('loading_options')
                        : t('about_role_area_placeholder')}
                    </option>
                    {workAreas.map((area) => {
                      const v = refId(area)
                      const lab = refLabel(area)
                      if (!v) return null
                      return (
                        <option key={v} value={v}>
                          {lab}
                        </option>
                      )
                    })}
                  </select>
                </div>
              )}
            />
            {errors.workArea?.message && (
              <ErrorMessage
                message={errors.workArea.message}
                className="text-left"
              />
            )}
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-sm font-semibold text-secondary">
              {t('about_role_seniority_label')}
            </p>
            <Controller
              control={control}
              name="seniorityLevel"
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {seniorityOptions.map((opt) => {
                    const selected = field.value === opt.value
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={isMutating}
                        onClick={() => field.onChange(opt.value)}
                        className={`rounded-xl border px-4 py-3 text-sm font-medium transition-all ${
                          selected
                            ? 'border-mydra-purple bg-mydra-purple-light text-mydra-purple'
                            : ' bg-white text-mydra-black hover:border-mydra-purple hover:text-mydra-purple'
                        } ${isMutating ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              )}
            />
            {errors.seniorityLevel?.message && (
              <ErrorMessage
                message={errors.seniorityLevel.message}
                className="text-left"
              />
            )}
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-sm font-semibold text-secondary">
              {t('about_role_industry_label')}
            </p>
            <Controller
              control={control}
              name="companyIndustry"
              render={() => (
                <div className="flex flex-wrap gap-3">
                  {industriesForGrid.map((industry) => {
                    const id = refId(industry)
                    if (!id) return null
                    const label = refLabel(industry)
                    const tone = industryIconTone(industry.slug)
                    const checked = selectedIndustryId === id
                    return (
                      <Checkbox
                        key={id}
                        id={`company-industry-${id}`}
                        checked={checked}
                        disabled={isMutating || listsLoading}
                        onChange={() => selectIndustry(id)}
                        variant="medium"
                        label={
                          <span className="flex min-w-0 items-center gap-3">
                            <span
                              className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-md ${tone.boxClass}`}
                            >
                              <CompanyIndustryListIcon
                                iconName={industry.iconName}
                                iconClass={tone.iconClass}
                              />
                            </span>
                            <Typography
                              variant="body2"
                              as="span"
                              className="font-medium"
                            >
                              {label}
                            </Typography>
                          </span>
                        }
                        labelProps={{
                          className: [
                            'inline-flex max-w-full min-h-[3.25rem] shrink-0 cursor-pointer items-center rounded-xl border bg-white p-2 flex-row-reverse gap-5 !font-normal',
                            '[&>div:first-child]:!flex-none [&>div:first-child]:shrink-0 [&>span]:!w-auto [&>span]:min-w-0',
                            checked
                              ? 'border-mydra-purple'
                              : 'hover:border-gray-300',
                            isMutating || listsLoading
                              ? 'cursor-not-allowed opacity-60'
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' '),
                        }}
                        glyphProps={{ className: 'text-gray-400' }}
                      />
                    )
                  })}
                </div>
              )}
            />
            {errors.companyIndustry?.message && (
              <ErrorMessage
                message={errors.companyIndustry.message}
                className="text-left"
              />
            )}
          </div>

          <div className="mt-auto flex w-full md:justify-end">
            <ProgressBar
              max={maxSteps}
              progress={currentStep}
              className="mr-auto self-center md:w-32"
            />
            <PrimaryButton
              type="submit"
              disabled={isMutating || listsLoading}
              isLoading={isSubmitting}
              className="w-full md:w-auto"
            >
              {t('continue')}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
